// Data-loading hook for the reporting page. Pulls the CDI overview, the CDI
// time series, each supporting indicator's time series (for the per-map charts),
// the impact-stats envelope, and product metadata for a given country / time
// step, and exposes a single loading + error surface.

import { useEffect, useMemo, useState } from "react";

import { fetchProductOverviews, fetchReportingStat } from "./api";
import { INDICATOR_SERIES } from "./reportConfig";

const SERIES_PARAMS = Object.keys(INDICATOR_SERIES);

/**
 * @param {{countryId: string|number, year: string|number, monthInt: string, dekad: string}} params
 * @returns {{
 *   cdiOverview: object|null,
 *   cdiSummary: Array|null,
 *   indicatorSeries: Record<string, Array|null>,
 *   impactStats: {available: boolean, reason?: string, phases: object}|null,
 *   productOverviews: Record<string, string>,
 *   loading: boolean,
 *   error: string|null,
 * }}
 */
export default function useReportData({ countryId, year, monthInt, dekad }) {
  const [cdiOverview, setCdiOverview] = useState(null);
  const [cdiSummary, setCdiSummary] = useState(null);
  const [indicatorSeries, setIndicatorSeries] = useState({});
  const [impactStats, setImpactStats] = useState(null);
  const [productOverviews, setProductOverviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const ready = Boolean(countryId && year && monthInt && dekad);

  useEffect(() => {
    if (!ready) return undefined;

    let cancelled = false;
    const base = { countryId, year, month: monthInt, dekad };

    setLoading(true);
    setError(null);
    setIndicatorSeries({});

    const overviewPromise = fetchReportingStat({ ...base, dataParam: "cdi_overview" })
      .then((res) => {
        if (!cancelled) setCdiOverview(res?.cdi || null);
      })
      .catch(() => {
        if (!cancelled) setCdiOverview(null);
      });

    const summaryPromise = fetchReportingStat({ ...base, dataParam: "cdi" })
      .then((res) => {
        if (!cancelled) setCdiSummary(res?.cdi_summary || null);
      })
      .catch(() => {
        if (!cancelled) setCdiSummary(null);
      });

    const seriesPromises = SERIES_PARAMS.map((param) =>
      fetchReportingStat({ ...base, dataParam: param })
        .then((res) => {
          if (cancelled) return;
          setIndicatorSeries((prev) => ({
            ...prev,
            [param]: res?.[INDICATOR_SERIES[param]] ?? null,
          }));
        })
        .catch(() => {
          if (!cancelled) setIndicatorSeries((prev) => ({ ...prev, [param]: null }));
        }),
    );

    fetchReportingStat({ ...base, dataParam: "impact_stats" })
      .then((res) => {
        if (!cancelled) setImpactStats(res?.impact_stats || null);
      })
      .catch(() => {
        if (!cancelled) setImpactStats(null);
      });

    fetchProductOverviews()
      .then((overviews) => {
        if (!cancelled) setProductOverviews(overviews);
      })
      .catch(() => {
        if (!cancelled) setProductOverviews({});
      });

    Promise.allSettled([overviewPromise, summaryPromise, ...seriesPromises]).then(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [ready, countryId, year, monthInt, dekad]);

  return useMemo(
    () => ({
      cdiOverview,
      cdiSummary,
      indicatorSeries,
      impactStats,
      productOverviews,
      loading,
      error,
    }),
    [cdiOverview, cdiSummary, indicatorSeries, impactStats, productOverviews, loading, error],
  );
}
